import { lazy, Suspense } from 'react'

const StreamMarkdownBody = lazy(() =>
  import('./StreamMarkdownBody.jsx').then((m) => ({
    default: m.StreamMarkdownBody,
  })),
)

function MarkdownFallback({ text }) {
  return (
    <p className="m-0 whitespace-pre-wrap break-words [&_a]:underline">
      {text || ''}
    </p>
  )
}

/**
 * Plain-text message renderer.
 *
 * Visitor messages are escaped (rendered as text content) because the
 * customer never marks them up. Bot messages support a small HTML
 * surface - the legacy server emits anchors and inline formatting -
 * so we use `dangerouslySetInnerHTML`. Streamed LLM turns set
 * `streamMarkdown` and render as GitHub-flavored Markdown instead
 * (loaded on demand so the default widget bundle stays smaller).
 *
 * Attachments (image/audio/video) follow the same rendering contract as
 * v1 so old admin-side message templates keep working unchanged.
 */
export function TextMessage({ message, conf }) {
  const attachment = message.attachment
  const useMarkdown = message.from !== 'visitor' && message.streamMarkdown

  return (
    <div className="ometrics-msg-text [&_img]:max-h-[50vh] [&_img]:max-w-full [&_img]:object-contain">
      {message.from === 'visitor' ? (
        <p className="m-0 whitespace-pre-wrap break-words">{message.text}</p>
      ) : useMarkdown ? (
        <Suspense fallback={<MarkdownFallback text={message.text} />}>
          <StreamMarkdownBody text={message.text} />
        </Suspense>
      ) : (
        <p
          className="m-0 whitespace-pre-wrap break-words [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: message.text || '' }}
        />
      )}

      {attachment && attachment.type === 'image' && attachment.url && (
        <img
          src={attachment.url}
          alt=""
          className="mt-2 max-h-[50vh] max-w-full rounded-md object-contain"
        />
      )}

      {attachment && attachment.type === 'audio' && attachment.url && (
        <audio controls className="mt-2 w-full">
          <source src={attachment.url} type="audio/mp3" />
        </audio>
      )}

      {attachment && attachment.type === 'video' && attachment.url && (
        <video
          controls
          className="mt-2 max-w-full rounded-md"
          height={conf?.videoHeight}
        >
          <source src={attachment.url} type="video/mp4" />
        </video>
      )}
    </div>
  )
}
